import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/** Revert all scoped animations on navigation, filter changes and reduced motion. */
export function usePageMotion<T extends HTMLElement>(changeKey = '') {
  const root = useRef<T>(null);

  useLayoutEffect(() => {
    if (!root.current) return;
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const scope = gsap.utils.selector(root);
      const heroLines = scope('[data-hero-line]');
      const heroItems = scope('[data-hero-reveal]');
      const visual = scope('[data-hero-visual]');
      const intro = gsap.timeline({ defaults: { ease: 'power3.out' } });
      if (heroLines.length) intro.from(heroLines, { yPercent: 110, rotate: 2, duration: 1.1, stagger: 0.12 });
      if (heroItems.length) intro.from(heroItems, { y: 22, opacity: 0, duration: 0.75, stagger: 0.1 }, heroLines.length ? 0.2 : 0);
      if (visual.length) intro.from(visual, { y: 32, opacity: 0, rotate: 2, duration: 1.2 }, 0.15);
      scope('[data-reveal]').forEach((element) => {
        gsap.from(element, { y: 32, opacity: 0, duration: 0.8, ease: 'power3.out', scrollTrigger: { trigger: element, start: 'top 92%', once: true } });
      });
      scope('[data-process-line]').forEach((element) => {
        gsap.from(element, { scaleY: 0, transformOrigin: 'top', ease: 'none', scrollTrigger: { trigger: element.parentElement, start: 'top 75%', end: 'bottom 65%', scrub: 0.5 } });
      });
    }, root);
    media.add('(min-width: 969px) and (prefers-reduced-motion: no-preference)', () => {
      gsap.utils.selector(root)('[data-parallax]').forEach((element) => {
        gsap.to(element, { y: -35, ease: 'none', scrollTrigger: { trigger: root.current, start: 'top top', end: '600px top', scrub: 1 } });
      });
    }, root);
    let mounted = true;
    document.fonts.ready.then(() => { if (mounted) ScrollTrigger.refresh(); });
    return () => { mounted = false; media.revert(); };
  }, [changeKey]);
  return root;
}
